<!-- Unified bottom bar: active timer control (top layer) + context bar (bottom layer). -->
<template>
  <div v-if="isVisible" class="bottom-bar">
    <div v-if="hasTimer" class="progress-container">
      <div
        class="progress-bar"
        :style="{ width: `${progressPercentage}%` }"
        :class="{
          'is-active-timer': !!store.activeTimer && progressPercentage < 100,
          'is-active-timer-complete': !!store.activeTimer && progressPercentage >= 100,
          'is-inactive-timer': !store.activeTimer && progressPercentage < 100,
          'is-inactive-timer-complete': !store.activeTimer && progressPercentage >= 100,
        }">
      </div>
    </div>

    <div class="bottom-bar__inner">
      <!-- Timer layer -->
      <div v-if="hasTimer" class="bottom-bar__timer" :class="{ 'is-timer-clickable': hasTimer }" @click="handleTimerClick">
        <div class="controls" @click.stop>
          <button v-if="!store.activeTimer"
                  class="button is-primary is-small"
                  @click="startTimer"
                  title="Start Timer">
            <span class="icon">
              <FontAwesomeIcon :icon="faPlay" />
            </span>
          </button>
          <button v-else
                  class="button is-link is-small"
                  @click="stopTimer"
                  title="Stop Timer">
            <span class="icon">
              <FontAwesomeIcon :icon="faStop" />
            </span>
          </button>
        </div>

        <div class="timer-info">
          <p class="title is-6 mb-0">
            {{ displayTitle }}
          </p>
          <p v-if="projectTagsLabel" class="subtitle is-7 mb-0 has-text-grey">{{ projectTagsLabel }}</p>
        </div>

        <button
          class="button is-small focus-trigger"
          @click.stop="store.openFocusMode()"
          title="Focus mode"
        >
          <span class="icon is-small">
            <FontAwesomeIcon :icon="faExpand" />
          </span>
        </button>

        <div class="timer-time">
          <p class="timer-time__elapsed mb-0" v-html="formatBoldTimecode(currentElapsedTimeInSeconds)"></p>
          <p class="timer-time__remaining mb-0"><small v-html="formatRemainingTime(currentRemainingTimeInSeconds)"></small></p>
        </div>
      </div>
      <div v-else-if="store.hasContextTimer" class="bottom-bar__timer bottom-bar__timer--empty">
        <p class="help bottom-bar__timer-empty-text mb-0">No timer currently scheduled or active</p>
      </div>

      <!-- Context layer -->
      <div v-if="store.hasContextTimer" class="bottom-bar__context" :class="{ 'is-active': !!store.activeContext }" @click.stop>
        <Transition name="context-content" mode="out-in">
          <div v-if="store.activeContext" key="active" class="context-active">
            <div class="context-active__meta">
              <span class="context-active__label">{{ store.activeContext.display_label }}</span>
              <span v-if="activeContextStartedAtLabel" class="context-active__started-at">started at {{ activeContextStartedAtLabel }}</span>
            </div>
            <span class="context-active__time">{{ formatCompactDuration(store.contextElapsedSeconds) }}</span>
            <ContextEndDropdown />
          </div>
          <div v-else-if="showContextInput" key="input" class="context-idle--input">
            <ContextInput @cancel="showContextInput = false" @started="showContextInput = false" />
          </div>
          <div v-else key="idle" class="context-idle">
            <button class="button is-small context-idle__add-button" @click="showContextInput = true" title="Start a context">
              <span class="icon is-small">
                <FontAwesomeIcon :icon="faPlus" />
              </span>
            </button>
            <span v-if="!recentContextButtons.length" class="context-idle__hint">Start a context...</span>
            <div v-if="recentContextButtons.length" class="context-idle__recent">
              <button
                v-for="context in recentContextButtons"
                :key="context.id"
                class="button is-small context-idle__recent-button"
                @click="startRecentContext(context)"
                :title="context.display_label"
              >
                {{ context.display_label }}
              </button>
            </div>
            <ContextHistoryDropdown @select="startRecentContext" @begin-in-past="openBeginInPast" @log-past="openLogPast" />
          </div>
        </Transition>
      </div>
    </div>
  </div>

  <ContextModal
    :isOpen="contextModalOpen"
    :context="contextModalContext"
    :mode="contextModalMode"
    :prefill="contextModalPrefill"
    @close="contextModalOpen = false"
    @saved="contextModalOpen = false"
    @deleted="contextModalOpen = false"
  />
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useBlocksStore } from '../stores/blocks'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faPlay, faStop, faPlus, faExpand } from '@fortawesome/free-solid-svg-icons'
import { formatBoldTimecode, formatCompactDuration, formatRemainingTime, formatTime } from '../utils/time'
import ContextInput from './ContextInput.vue'
import ContextHistoryDropdown from './ContextHistoryDropdown.vue'
import ContextEndDropdown from './ContextEndDropdown.vue'
import ContextModal from './ContextModal.vue'

const store = useBlocksStore()
const showContextInput = ref(false)
const contextModalOpen = ref(false)
const contextModalContext = ref(null)
const contextModalMode = ref('begin-in-past')
const contextModalPrefill = ref(null)

const hasTimer = computed(() => !!store.activeTimer || !!store.currentlyDueTimer)
const isVisible = computed(() => hasTimer.value || store.hasContextTimer)

const currentTimer = computed(() => store.activeTimer || store.currentlyDueTimer)

const recentContextButtons = computed(() => {
  const seenLabels = new Set()

  return (store.recentContexts || [])
    .filter(context => !context.running)
    .filter(context => {
      if (!context.display_label || seenLabels.has(context.display_label)) return false
      seenLabels.add(context.display_label)
      return true
    })
    .slice(0, 4)
})

const isSameDay = (dateString) => {
  const date = new Date(dateString)
  const today = new Date()

  return date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
}

const formatShortDate = (dateString) => {
  return new Date(dateString).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  })
}

const activeContextStartedAtLabel = computed(() => {
  if (!store.activeContext?.started_at) return ''

  const startedAt = store.activeContext.started_at
  const formattedTime = formatTime(new Date(startedAt))

  if (isSameDay(startedAt)) return formattedTime

  return `${formattedTime} (${formatShortDate(startedAt)})`
})

const displayTitle = computed(() => {
  const timer = currentTimer.value
  if (!timer) return ''
  return timer.title.length > 30 ? timer.title.substring(0, 30) + '…' : timer.title
})

const projectTagsLabel = computed(() => {
  const timer = currentTimer.value
  if (!timer) return ''
  const parts = []
  if (timer.project) parts.push(`@${timer.project.name}`)
  if (timer.tags?.length) parts.push(...timer.tags.map(t => `#${t.name}`))
  return parts.join(' ')
})

const calculateTotalSeconds = () => {
  if (!currentTimer.value) return 0
  return (new Date(currentTimer.value.endDate) - new Date(currentTimer.value.startDate)) / 1000
}

const currentElapsedTimeInSeconds = computed(() => {
  if (store.activeTimer) return store.elapsedTimeInSeconds
  return currentTimer.value?.duration || 0
})

const currentRemainingTimeInSeconds = computed(() => {
  if (!currentTimer.value) return 0
  if (store.activeTimer) return store.remainingTimeInSeconds
  return currentElapsedTimeInSeconds.value - calculateTotalSeconds()
})

const progressPercentage = computed(() => {
  if (!currentTimer.value) return 0
  const totalSeconds = calculateTotalSeconds()
  if (!totalSeconds) return 0
  return Math.min(100, Math.round((currentElapsedTimeInSeconds.value / totalSeconds) * 100))
})

const stopTimer = () => {
  if (store.activeTimer) {
    store.stopTimer(store.activeTimer.id)
  }
}

const startTimer = () => {
  if (store.currentlyDueTimer) {
    store.startTimer(store.currentlyDueTimer.id)
  }
}

const handleTimerClick = () => {
  if (!hasTimer.value) return
  const timerId = store.activeTimer?.id || store.currentlyDueTimer?.id
  if (timerId) {
    store.showModal(timerId)
  }
}

const startRecentContext = (context) => {
  store.startContext({
    projectId: context.project?.id,
    tagIds: context.tags?.map(tag => tag.id) || [],
  })
}

const openBeginInPast = (context) => {
  contextModalContext.value = null
  contextModalMode.value = 'begin-in-past'
  contextModalPrefill.value = context
  contextModalOpen.value = true
}

const openLogPast = () => {
  contextModalContext.value = null
  contextModalMode.value = 'log-completed'
  contextModalPrefill.value = null
  contextModalOpen.value = true
}

let intervalId
onMounted(() => {
  intervalId = setInterval(() => {
    store.updateCurrentTime()
  }, 1000)
})

onUnmounted(() => {
  clearInterval(intervalId)
})
</script>

<style scoped>
.bottom-bar {
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: min(960px, calc(100vw - 2rem));
  max-width: 100%;
  background: var(--bulma-scheme-main);
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  border-radius: 4px 4px 0 0;
  overflow: visible;
  z-index: 30;
  padding: 0.5rem 1rem;
  padding-bottom: max(0.5rem, env(safe-area-inset-bottom));
}

.bottom-bar__timer.is-timer-clickable {
  cursor: pointer;
}

.bottom-bar__inner {
  width: 100%;
  border-radius: inherit;
  overflow: visible;
}

.progress-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background-color: var(--bulma-background);
  border-radius: 4px 4px 0 0;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  transition: width 0.5s ease;
}

.progress-bar.is-active-timer {
  background-color: var(--bulma-link);
}

.progress-bar.is-active-timer-complete {
  background-color: var(--bulma-success);
  opacity: 0.85;
}

.progress-bar.is-inactive-timer {
  background-color: var(--bulma-primary-soft);
}

.progress-bar.is-inactive-timer-complete {
  background-color: var(--bulma-success-soft);
  opacity: 0.7;
}

.bottom-bar__timer {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-height: 3rem;
  padding-top: 0.2rem;
}

.bottom-bar__timer--empty {
  padding: 0.2rem 0;
  min-height: 2rem;
}

.bottom-bar__timer-empty-text {
  font-style: italic;
  line-height: 1.2;
}

.controls {
  flex-shrink: 0;
}

.focus-trigger {
  flex-shrink: 0;
}

.timer-info {
  flex: 1;
  min-width: 0;
}

.timer-info .title,
.timer-info .subtitle {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.timer-time {
  flex-shrink: 0;
  text-align: right;
}

.timer-time__elapsed,
.timer-time__remaining {
  line-height: 1.1;
}

.timer-time__remaining {
  color: var(--bulma-text-weak, var(--bulma-text));
}

/* Context layer */
.bottom-bar__context {
  --context-idle-bg: rgba(213, 99, 45, 0.045);
  --context-idle-chip-bg: rgba(213, 99, 45, 0.08);
  --context-idle-chip-hover-bg: rgba(213, 99, 45, 0.13);
  --context-idle-chip-text: hsla(19deg, 67%, 34%, 0.92);
  --context-idle-chip-border: rgba(213, 99, 45, 0.12);
  --context-surface-bg: rgba(213, 99, 45, 0.08);
  --context-surface-border: rgba(213, 99, 45, 0.16);
  --context-surface-shadow: rgba(213, 99, 45, 0.12);
  --context-surface-text: hsla(19deg, 67%, 28%, 1);
  --context-surface-muted: hsla(19deg, 67%, 28%, 0.72);
  --context-surface-strong: hsla(19deg, 67%, 28%, 0.8);
  --context-button-bg: rgba(213, 99, 45, 0.12);
  --context-button-hover-bg: rgba(213, 99, 45, 0.18);
  position: relative;
  margin: 0.5rem -1rem -0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(213, 99, 45, 0.08);
  min-height: 3rem;
  transition: background-color 180ms ease, box-shadow 180ms ease;
}

.bottom-bar__context:not(.is-active) {
  background: var(--context-idle-bg);
}

.bottom-bar__context::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  border-top: 1px solid var(--bulma-border);
  pointer-events: none;
  transition: border-color 180ms ease;
}

.bottom-bar__context.is-active {
  background: var(--context-surface-bg);
  box-shadow: inset 0 1px 0 var(--context-surface-shadow);
}

.bottom-bar__context.is-active::before {
  border-top-color: var(--context-surface-border);
}

.context-idle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  min-height: 2rem;
}


.context-idle__hint {
  font-size: 0.85rem;
  color: var(--bulma-text-weak);
  flex-shrink: 0;
}

.context-idle__recent {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
  overflow-x: auto;
  padding-bottom: 0.1rem;
}

.context-idle__recent-button {
  flex-shrink: 0;
  max-width: 14rem;
  overflow: hidden;
  text-overflow: ellipsis;
  background: transparent;
  border-color: var(--context-idle-chip-border);
  color: var(--context-idle-chip-text);
}

.context-idle__recent-button:hover {
  background: var(--context-idle-chip-hover-bg);
  border-color: var(--context-idle-chip-border);
  color: var(--context-idle-chip-text);
}

.context-idle__add-button {
  flex-shrink: 0;
  background: transparent;
  border-color: var(--context-idle-chip-border);
  color: var(--context-idle-chip-text);
}

.context-idle__add-button:hover {
  background: var(--context-idle-chip-hover-bg);
  border-color: var(--context-idle-chip-border);
  color: var(--context-idle-chip-text);
}

.context-idle--input {
  display: flex;
  align-items: center;
  position: relative;
  min-width: 0;
  min-height: 2rem;
  z-index: 2;
}

.context-active {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  min-height: 2rem;
}

.context-content-enter-active,
.context-content-leave-active {
  transition: opacity 140ms ease, transform 180ms ease;
}

.context-content-enter-from,
.context-content-leave-to {
  opacity: 0;
  transform: translateY(2px);
}

.context-active__meta {
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.context-active__label {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--context-surface-text);
}

.context-active__started-at {
  margin-left: 0.45rem;
  font-size: 0.8rem;
  font-weight: 400;
  color: var(--context-surface-muted);
  font-style: italic;
}

.context-active__time {
  flex-shrink: 0;
  font-size: 0.85rem;
  color: var(--context-surface-strong);
  min-width: 3.2rem;
  text-align: right;
}

.context-active__stop {
  flex-shrink: 0;
  background: var(--context-button-bg);
  border-color: var(--context-surface-border);
  color: var(--timist-context-orange, #d5632d);
}

.context-active__stop:hover {
  background: var(--context-button-hover-bg);
}

@media (prefers-color-scheme: dark) {
  .bottom-bar__context {
    --context-idle-bg: rgba(213, 99, 45, 0.035);
    --context-idle-chip-bg: rgba(213, 99, 45, 0.07);
    --context-idle-chip-hover-bg: rgba(213, 99, 45, 0.11);
    --context-idle-chip-text: hsla(19deg, 72%, 72%, 0.88);
    --context-idle-chip-border: rgba(213, 99, 45, 0.08);
    --context-surface-bg: rgba(213, 99, 45, 0.13);
    --context-surface-border: rgba(213, 99, 45, 0.24);
    --context-surface-shadow: rgba(213, 99, 45, 0.18);
    --context-surface-text: hsl(19deg, 72%, 74%);
    --context-surface-muted: hsla(19deg, 72%, 74%, 0.74);
    --context-surface-strong: hsla(19deg, 72%, 78%, 0.82);
    --context-button-bg: rgba(213, 99, 45, 0.18);
    --context-button-hover-bg: rgba(213, 99, 45, 0.24);
  }
}

@media (max-width: 768px) {
  .bottom-bar {
    left: 0;
    right: 0;
    transform: none;
    width: 100%;
    max-width: none;
    border-radius: 0;
  }
}
</style>
